import { findFirstNonEmptyString } from "@product/scmp-sdk";
import capitalize from "lodash/capitalize";
import first from "lodash/first";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { checkIsStyleArticle } from "~/components/article/article-render/style-article/helper";
import { useContentHelper } from "~/components/content/hooks";
import { section as sectionData } from "~/data/section";
import type {
  hooksContentHeadlineTagContent$data,
  hooksContentHeadlineTagContent$key,
} from "~/queries/__generated__/hooksContentHeadlineTagContent.graphql";

type HeadlineTagData = {
  color?: string;
  description?: string;
  entityId?: string;
  name?: string;
};

type Type = "column" | "flag" | "opinion" | "section" | "specialCase";

export const useDetermineHeadlineTag = (
  reference_: hooksContentHeadlineTagContent$key,
  useSectionTags = false,
) => {
  const content = useFragment(
    graphql`
      fragment hooksContentHeadlineTagContent on Content {
        ...hooksContentHelperContent
        ... on Article {
          ...helpersStyleArticle
          types {
            value {
              name
              entityId
              description {
                text
              }
            }
          }
        }
        flag
        sections {
          value {
            name
          }
        }
      }
    `,
    reference_,
  );

  const { isContentWithFlag, isEditorialOpinionType, isScmpOpinionType, isSpecialArticleType } =
    useContentHelper(content);

  const headlineTagType = useMemo<Type | null>(() => {
    if (isScmpOpinionType) return "opinion";
    else if (isEditorialOpinionType) return "column";
    else if (isContentWithFlag) return "flag";
    else if (isSpecialArticleType) return "specialCase";
    else if (useSectionTags) return "section";
    else return null;
  }, [
    isContentWithFlag,
    isScmpOpinionType,
    isEditorialOpinionType,
    isSpecialArticleType,
    useSectionTags,
  ]);

  const computeHeaderTagData = (
    reference: hooksContentHeadlineTagContent$data,
    type: Type,
  ): HeadlineTagData => {
    const flattenedTypes = (reference.types ?? [])
      .flatMap(type => type?.value)
      .map(type => ({
        description: type?.description?.text ?? "",
        entityId: type?.entityId ?? "",
        name: type?.name ?? "",
      }));

    const firstType = first(flattenedTypes);
    const secondType = flattenedTypes.length > 1 ? flattenedTypes[1] : firstType;

    const flattenedSections =
      reference.sections
        ?.flatMap(currentSection => currentSection?.value)
        .map(currentSection => currentSection?.name)
        .filter(currentSection => currentSection !== sectionData.news.name)
        .filter(currentSection => currentSection !== sectionData.postMagazine.name) ?? [];

    return {
      column: {
        color: "#34b8b5",
        description: secondType?.description ?? "",
        entityId: secondType?.entityId ?? "",
        name: secondType?.name ?? "",
      },
      flag: {
        color: "#c79a00",
        name: capitalize(reference?.flag ?? ""),
      },
      opinion: {
        color: checkIsStyleArticle(reference) ? "#222222" : "#34b8b5",
        description: firstType?.description ?? "",
        entityId: firstType?.entityId ?? "",
        name: "Opinion",
      },
      section: {
        color: "#666",
        name: findFirstNonEmptyString(...flattenedSections),
      },
      specialCase: {
        color: "#c79a00",
        description: firstType?.description ?? "",
        entityId: firstType?.entityId ?? "",
        name: firstType?.name ?? "",
      },
    }[type];
  };

  return headlineTagType ? computeHeaderTagData(content, headlineTagType) : null;
};
